import type { AiDebugPayload, PendingCards } from '@digital-cs/shared';
import type { IWsClient } from '../types';
import { dcsLog } from './logger';
import { printAiDebug } from './ai-debug-format';

/**
 * 网页客服传输客户端 (SSE + HTTP POST)
 *
 * 对外保持 `on / off / emit / connect / disconnect` 接口签名 (沿用历史 IWsClient 命名),
 * 内部实现：
 *  - 访客发消息走 `POST /api/public/widget/message`
 *  - AI 回复 / 系统通知 / 留资卡 / 工单通知走 `GET /api/public/widget/stream` (EventSource)
 *  - 第一次 connect / emit 时会自动 `POST /api/public/widget/session` 拿 visitorToken+convId
 *
 * v1 不用 WebSocket：AI 网关本身就是 SSE，BE 直接透传，链路最短；
 * 部署不需要 WS 反代特殊配置；后续要做实时坐席接管时再为 admin 加 WS。
 */

type Handler = (payload: unknown) => void;

interface TransportInit {
  apiBase: string;
  clientId: string;
  fingerprint: string;
  locale: string;
  /** 访客发起会话所在的宿主页 URL（bootstrap 时固化 location.href）；建会话时上报，推主站 sourceUrl */
  pageUrl?: string;
  /**
   * 宿主商城登录 token 取值器（main.tsx 注入 readHostToken）。
   * 每次发消息即时调用拿最新 token（token 会过期/换号）；未登录返回 null。
   * 用回调而非固定值，保持 ws-client 不直接依赖 DOM、便于单测。
   */
  hostTokenProvider?: () => string | null;
}

export interface SessionHistoryItem {
  id: string;
  role: string;
  content: string;
  msgType: string;
  createdAt: string;
}

interface SessionResponse {
  visitorToken: string;
  convId: string;
  history: SessionHistoryItem[];
  welcomeMsg: string;
  quickQuestions: unknown[];
  pendingCards: PendingCards | null;
  /** 在飞回合快照(无则 null):刷新后恢复忙碌闸门 + 续传流式气泡,见 BE turn.active */
  activeTurn?: { messageId: string | null; text: string } | null;
}

const SSE_EVENT_NAMES = [
  'ai.stream.start',
  'ai.stream.delta',
  'ai.stream.end',
  'turn.active',
  'ai.audio',
  'ai.error',
  'ai.debug',
  'system.notice',
  'lead.card.show',
  'ticket.created',
  'heartbeat',
];

class SseTransportClient implements IWsClient {
  private listeners = new Map<string, Set<Handler>>();
  private init: TransportInit | null = null;
  private visitorToken: string | null = null;
  private convId: string | null = null;
  private session: SessionResponse | null = null;
  private sessionPromise: Promise<SessionResponse> | null = null;
  private eventSource: EventSource | null = null;
  private connected = false;

  configure(init: TransportInit): void {
    // apiBase 已经带 /api 前缀（与 widget-config-client 用法一致），统一去尾 slash
    this.init = { ...init, apiBase: init.apiBase.replace(/\/+$/, '') };
  }

  /**
   * 访客切换语言后更新当前 locale —— 后续建会话 / 发消息即带新语言码，
   * 后端据此切 AI 回复 / TTS 语言。与 vocechat-client.setLanguage 同款（动态而非固化）。
   */
  setLocale(locale: string): void {
    if (this.init) this.init.locale = locale;
  }

  on(event: string, handler: Handler): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: string, handler: Handler): void {
    this.listeners.get(event)?.delete(handler);
  }

  emit(event: string, payload: unknown): void {
    if (event === 'visitor.message') {
      void this.sendVisitorMessage(payload as Record<string, unknown>).catch((err) => {
        dcsLog.warn('transport', '发送访客消息失败', err);
      });
    } else if (event === 'visitor.lead.submit') {
      void this.sendLead(payload as Record<string, unknown>).catch((err) => {
        dcsLog.warn('transport', '提交留资信息失败', err);
      });
    } else {
      dcsLog.warn('transport', `未识别的 emit 事件: ${event}`, payload);
    }
  }

  async connect(): Promise<void> {
    if (this.connected) return;
    await this.ensureSession();
    this.openStream();
    this.connected = true;
  }

  disconnect(): void {
    this.eventSource?.close();
    this.eventSource = null;
    this.connected = false;
    this.listeners.clear();
  }

  /** 拉起会话后的初始数据(供面板组件灌历史/还原 lead pending) */
  getInitialSession(): SessionResponse | null {
    return this.session;
  }

  /**
   * 访客点"重新开始" —— 关掉旧 conv,清空内部缓存,下次 connect/emit 时
   * 会自动 POST /session 拿到新的 convId+token,SSE 也会重新建。
   */
  async resetSession(): Promise<void> {
    if (this.init && this.visitorToken) {
      try {
        await fetch(`${this.init.apiBase}/public/widget/session/reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.visitorToken}`,
          },
          credentials: 'omit',
        });
      } catch (err) {
        // 网络层失败不阻塞前端重置(下一次 /session 自然新建)
        dcsLog.warn('transport', 'POST /session/reset failed', err);
      }
    }
    this.eventSource?.close();
    this.eventSource = null;
    this.connected = false;
    this.session = null;
    this.visitorToken = null;
    this.convId = null;
  }

  /**
   * 访客 dismiss 留资卡 —— 把后端 pending_cards.lead.dismissed 置 true,
   * 下次打开 widget 时按 lead-reopen pill 还原入口。
   */
  async dismissLeadCard(): Promise<void> {
    if (!this.init || !this.visitorToken) return;
    try {
      await fetch(`${this.init.apiBase}/public/widget/lead/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.visitorToken}`,
        },
        credentials: 'omit',
      });
    } catch (err) {
      dcsLog.warn('transport', 'POST /lead/dismiss failed', err);
    }
  }

  /** 供 mock / 单测用 */
  _dispatch(event: string, payload: unknown): void {
    this.listeners.get(event)?.forEach((h) => h(payload));
  }

  // ── 内部 ──

  private async ensureSession(): Promise<SessionResponse> {
    if (this.session) return this.session;
    if (this.sessionPromise) return this.sessionPromise;
    if (!this.init) {
      throw new Error(
        '[transport] not configured — call configureWsClient({apiBase,clientId,fingerprint,locale}) before connect/emit',
      );
    }
    const { apiBase, clientId, fingerprint, locale, pageUrl } = this.init;
    this.sessionPromise = (async () => {
      const res = await fetch(`${apiBase}/public/widget/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          fingerprint,
          locale,
          ...(pageUrl ? { pageUrl } : {}),
        }),
        credentials: 'omit',
      });
      if (!res.ok) {
        throw new Error(`session ${res.status}`);
      }
      const wrapped = (await res.json()) as { code: number; data: SessionResponse };
      const sess = wrapped.data;
      this.session = sess;
      this.visitorToken = sess.visitorToken;
      this.convId = sess.convId;
      return sess;
    })();
    try {
      return await this.sessionPromise;
    } finally {
      this.sessionPromise = null;
    }
  }

  private openStream(): void {
    if (!this.init || !this.visitorToken || !this.convId) return;
    if (this.eventSource) return;
    const url = `${this.init.apiBase}/public/widget/stream?convId=${encodeURIComponent(
      this.convId,
    )}&token=${encodeURIComponent(this.visitorToken)}`;
    const es = new EventSource(url);
    this.eventSource = es;
    for (const name of SSE_EVENT_NAMES) {
      es.addEventListener(name, (ev: MessageEvent) => {
        try {
          const data = ev.data ? JSON.parse(ev.data) : {};
          // ai.error 仅供联调，控制台输出，不下发到任何业务监听器（不入聊天 UI）
          if (name === 'ai.error') {
            dcsLog.warn('ai.error', data);
            return;
          }
          // ai.debug 仅在 BE DEBUG_MODE=true 时下发；走 printAiDebug 格式化输出
          // （每字段附中文含义 + 备选值集），不入聊天 UI。
          if (name === 'ai.debug') {
            printAiDebug(data as AiDebugPayload);
            return;
          }
          this.listeners.get(name)?.forEach((h) => h(data));
        } catch (err) {
          dcsLog.warn('transport', `解析 SSE 事件 ${name} 失败`, err);
        }
      });
    }
    es.addEventListener('error', () => {
      // EventSource 自带 reconnect；仅在彻底关闭时清理
      if (es.readyState === EventSource.CLOSED) {
        this.eventSource = null;
        this.connected = false;
      }
    });
  }

  private async sendVisitorMessage(payload: Record<string, unknown>): Promise<void> {
    await this.ensureSession();
    this.openStream();
    if (!this.init || !this.visitorToken || !this.convId) return;
    // 即时读宿主商城登录 token；有则带上（供 BE 调主站订单/工单 API），未登录省略
    const hostToken = this.init.hostTokenProvider?.() ?? null;
    const body = {
      convId: this.convId,
      text: payload.text as string | undefined,
      msgType: (payload.msgType as string) ?? 'text',
      asset: payload.asset,
      // 当前会话语言（随访客切换语言动态更新）；后端据此更新 conversation.locale，
      // 让 AI 回复 / 数字人 TTS 跟随访客最新选择的语言。
      locale: this.init.locale,
      ...(hostToken ? { hostToken } : {}),
    };
    const res = await fetch(`${this.init.apiBase}/public/widget/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.visitorToken}`,
      },
      body: JSON.stringify(body),
      credentials: 'omit',
    });
    if (!res.ok) {
      dcsLog.warn('transport', `POST /message 失败: ${res.status}`);
    }
  }

  private async sendLead(payload: Record<string, unknown>): Promise<void> {
    await this.ensureSession();
    if (!this.init || !this.visitorToken || !this.convId) return;
    const res = await fetch(`${this.init.apiBase}/public/widget/lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.visitorToken}`,
      },
      body: JSON.stringify({ convId: this.convId, ...payload }),
      credentials: 'omit',
    });
    if (!res.ok) {
      dcsLog.warn('transport', `POST /lead 失败: ${res.status}`);
    }
  }
}

let instance: IWsClient | null = null;

export function getWsClient(): IWsClient {
  if (!instance) instance = new SseTransportClient();
  return instance;
}

export function setWsClient(client: IWsClient): void {
  instance = client;
}

/** 配置传输层 (apiBase / clientId / fingerprint / locale)，在 bootstrap 时调用一次 */
export function configureWsClient(init: TransportInit): void {
  const client = getWsClient();
  if (client instanceof SseTransportClient) {
    client.configure(init);
  }
}

/**
 * 访客切换语言后同步当前 locale —— 后续发消息 / 建会话即带新语言码。
 * 与 vocechat-client 的 setImClientLanguage 对称，由 main.tsx 的 effect 跟随 currentLocale 调用。
 */
export function setWsClientLocale(locale: string): void {
  const c = getWsClient();
  if (c instanceof SseTransportClient) c.setLocale(locale);
}

/** 仅测试用 — 让单测之间互不污染 */
export function resetWsClient(): void {
  if (instance && 'disconnect' in instance) instance.disconnect();
  instance = null;
}

/**
 * 取出 connect() 完成后的初始会话数据(history + pendingCards + activeTurn 等)。
 * 仅 SseTransportClient 提供;mock 实现无此方法时返回 null。
 */
export function getInitialSession(): {
  history: SessionHistoryItem[];
  pendingCards: PendingCards | null;
  /** 在飞回合快照(无则 null)—— 刷新后恢复忙碌闸门/续传气泡用 */
  activeTurn: { messageId: string | null; text: string } | null;
} | null {
  const c = getWsClient();
  if (c instanceof SseTransportClient) {
    const sess = c.getInitialSession();
    if (!sess) return null;
    return {
      history: sess.history,
      pendingCards: sess.pendingCards,
      activeTurn: sess.activeTurn ?? null,
    };
  }
  return null;
}

/** 触发"重新开始" —— 见 SseTransportClient.resetSession 注释 */
export async function resetSession(): Promise<void> {
  const c = getWsClient();
  if (c instanceof SseTransportClient) await c.resetSession();
}

/** 通知后端访客 dismiss 了留资卡 */
export async function dismissLeadCard(): Promise<void> {
  const c = getWsClient();
  if (c instanceof SseTransportClient) await c.dismissLeadCard();
}
